import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Cpu,
  Package,
  Server,
  SlidersHorizontal,
  Terminal,
} from 'lucide-react';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { DirtyActionBar } from '@reflex/ui/components/shared/DirtyActionBar';
import { CanGuard } from '@reflex/ui/client/permissions';
import { InlineDocsHint } from '@reflex/ui/components/layout/Typography';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@reflex/ui/components/ui/collapsible';
import { cn } from '@reflex/ui/lib/utils';
import { CommandBlock } from '@reflex/ui/components/content/CommandBlock';
import { EditableCommandList } from '@reflex/ui/components/content/EditableCommandList';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import type { BlueprintArchitecture } from '@/lib/types';
import { CollapsibleSection } from '@/components/shared/CollapsibleSection';
import { BaseDockerfileDialog } from './BaseDockerfileDialog';
import { useBaseImage, useUpdateBaseImageOverrides } from '@/hooks/blueprints/useBaseImage';

/**
 * Replaces the flat "Setup Commands" section for the host-managed base
 * blueprint. Composed inline inside `BlueprintDetailPanel` so the
 * surrounding sections (Details, Dockerfile, File Mounts, Build Logs)
 * still render around it.
 *
 * The base blueprint's `system_setup_commands` is the concatenation of
 * three layers that operators must reason about independently:
 *
 *   1. Host core (read-only, each command annotated with a description)
 *   2. plugin layers (read-only, grouped by contributing plugin)
 *   3. Organization overrides (editable, appended last)
 *
 * To keep the panel scannable, the two read-only layers are collapsed
 * behind compact summary cards (count + one-line description) so an
 * operator can drill into the exact commands only when they need to.
 * The Organization overrides editor stays expanded and visually
 * accented — it is the only way to customize the host-managed base
 * (the detail panel hides Edit & rebuild for it), so it owns the
 * prominent slot even though its commands execute last.
 *
 * Saving overrides triggers a base rebuild and cascade-rebuild of every
 * dependent blueprint, so edits commit through a discrete "Save
 * overrides" button rather than auto-saving on each keystroke.
 */
export function BaseBlueprintLayersSection() {
  const { data, isLoading, isError } = useBaseImage();
  const update = useUpdateBaseImageOverrides();

  const serverCommands = data?.orgOverrideCommands;
  const serverArchitecture = data?.architecture;
  const [overrideCommands, setOverrideCommands] = useState<string[]>(() => serverCommands ?? []);
  const [architecture, setArchitecture] = useState<BlueprintArchitecture>(
    () => serverArchitecture ?? 'x86_64',
  );
  const [syncedFrom, setSyncedFrom] = useState<string[] | undefined>(serverCommands);
  const [newCommand, setNewCommand] = useState('');

  // Reseed local editor state when the server-side override list
  // changes (initial load, after a successful save, or another
  // operator's edit). Done during render rather than via useEffect —
  // see React docs "Adjusting state when a prop changes".
  if (serverCommands && serverCommands !== syncedFrom) {
    setSyncedFrom(serverCommands);
    setOverrideCommands(serverCommands);
    setArchitecture(serverArchitecture ?? 'x86_64');
  }

  if (isLoading) {
    return (
      <CollapsibleSection
        title="Setup Commands"
        icon={<Terminal className="h-3.5 w-3.5 text-muted-foreground/60" />}
      >
        <div className="flex items-center justify-center py-4 text-muted-foreground">
          <Spinner className="size-4" />
        </div>
      </CollapsibleSection>
    );
  }

  if (isError || !data) {
    return (
      <CollapsibleSection
        title="Setup Commands"
        icon={<Terminal className="h-3.5 w-3.5 text-muted-foreground/60" />}
      >
        <div className="flex items-center gap-2 rounded border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <span>Failed to load base blueprint layers.</span>
        </div>
      </CollapsibleSection>
    );
  }

  function handleSave() {
    const pending = newCommand.trim() ? [...overrideCommands, newCommand.trim()] : overrideCommands;
    // Architecture is deliberately omitted: the server keeps the stored value,
    // so a command-only save can never race a concurrent architecture change.
    update.mutate(
      { orgOverrideCommands: pending },
      {
        onSuccess: () => {
          toast.success('Base blueprint overrides saved. Rebuild scheduled.');
          setNewCommand('');
        },
        onError: (err: Error) =>
          toast.error('Failed to save overrides', { description: err.message }),
      },
    );
  }

  function handleDiscard() {
    setOverrideCommands(data?.orgOverrideCommands ?? []);
    setNewCommand('');
  }

  function handleSaveArchitecture() {
    // The architecture card saves independently of the command editor: it
    // replays the server's current commands so unsaved command edits in the
    // Organization layers card are neither committed nor lost.
    update.mutate(
      { orgOverrideCommands: data?.orgOverrideCommands ?? [], architecture },
      {
        onSuccess: () => {
          toast.success('Base image architecture saved. Rebuild scheduled.');
        },
        onError: (err: Error) =>
          toast.error('Failed to save architecture', { description: err.message }),
      },
    );
  }

  function handleDiscardArchitecture() {
    setArchitecture(data?.architecture ?? 'x86_64');
  }

  const architectureDirty = architecture !== data.architecture;
  const dirty =
    overrideCommands.length !== data.orgOverrideCommands.length ||
    overrideCommands.some((cmd, i) => cmd !== data.orgOverrideCommands[i]) ||
    newCommand.trim().length > 0;

  const pluginCommandCount = data.pluginContributions.reduce(
    (sum, contrib) => sum + contrib.commands.length,
    0,
  );

  return (
    <CollapsibleSection title="Image Layers">
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          <span className="font-mono text-foreground/90">base</span> layers three sources of setup,
          applied in order: the <span className="font-medium text-foreground">core layers</span>,
          then <span className="font-medium text-foreground">plugin layers</span> from your
          installed plugins, then your{' '}
          <span className="font-medium text-foreground">organization layers</span>. It currently
          runs{' '}
          <span className="font-medium text-foreground">
            {data.mergedSetupCommands.length}{' '}
            {data.mergedSetupCommands.length === 1 ? 'command' : 'commands'}
          </span>{' '}
          in total. Changing any layer rebuilds base, and every blueprint built on it rebuilds
          automatically.
        </p>

        {data.rebuildPending && (
          <div className="flex items-start gap-2 rounded-md border border-status-warning/30 bg-status-warning/10 px-3 py-2 text-sm">
            <AlertTriangle className="h-4 w-4 text-status-warning shrink-0 mt-0.5" />
            <span>
              The merged configuration differs from the latest completed build. A rebuild will run
              on the next bootstrap cycle, or now if you save overrides.
            </span>
          </div>
        )}

        {/* Read-only layers, collapsed by default. Expanding reveals an
            at-a-glance list of short labels; each label expands again to
            its command + description, so detail is opt-in at every level. */}
        <LayerCard
          icon={<Server />}
          name="Reflex base layers"
          description="The core environment every devbox starts from, managed by the host, including Docker-in-Docker support. Read-only."
          docsSlug="https://docs.runloop.ai/docs/devboxes/blueprints/dockerfile-customization"
          count={data.coreSetupCommands.length}
        >
          {data.coreSetupCommands.length === 0 ? (
            <p className="text-sm text-muted-foreground">None.</p>
          ) : (
            <div className="space-y-0.5">
              {data.coreSetupCommands.map((entry, i) => (
                <DisclosureRow
                  key={`core-${i}`}
                  label={entry.label ?? entry.description}
                  description={entry.label ? entry.description : undefined}
                >
                  <CommandBlock commands={entry.command} compact inline />
                </DisclosureRow>
              ))}
            </div>
          )}
        </LayerCard>

        <LayerCard
          icon={<Package />}
          name="Plugin layers"
          description="Setup commands contributed by your installed plugins. Uninstall a plugin to remove its commands."
          docsSlug="https://docs.runloop.ai/docs/devboxes/blueprints/overview"
          count={pluginCommandCount}
        >
          {data.pluginContributions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No installed plugins contribute setup commands.
            </p>
          ) : (
            <div className="space-y-0.5">
              {data.pluginContributions.map((contrib) => (
                <DisclosureRow
                  key={contrib.pluginName}
                  label={humanizePluginName(contrib.pluginName)}
                  description={contrib.description}
                  trailing={
                    contrib.commands.length > 1 ? (
                      <span className="rounded-full bg-muted px-1.5 py-px text-xs font-medium tabular-nums text-muted-foreground">
                        {contrib.commands.length}
                      </span>
                    ) : undefined
                  }
                >
                  <CommandBlock commands={contrib.commands} compact inline />
                </DisclosureRow>
              ))}
            </div>
          )}
        </LayerCard>

        {/* Org overrides (editable) — prominent, always expanded. Executes
            last in the merged list but is the only customizable layer. */}
        <LayerCard
          icon={<SlidersHorizontal />}
          name="Organization layers"
          description="Your organization's own commands, applied last. Saving rebuilds base and every blueprint built on it."
          docsSlug="https://docs.runloop.ai/docs/devboxes/blueprints/overview#creating-a-blueprint"
          defaultOpen
          accent
          count={overrideCommands.length}
        >
          {/* Editing requires `blueprints:write` — the same permission the
              override update endpoint enforces. Viewers without it see the
              overrides read-only and no save footer. */}
          <CanGuard
            permission="blueprints:write"
            fallback={
              overrideCommands.length > 0 ? (
                <CommandBlock commands={overrideCommands} compact />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No organization overrides. Requires blueprint access to edit.
                </p>
              )
            }
          >
            <EditableCommandList
              commands={overrideCommands}
              onCommandsChange={setOverrideCommands}
              draft={newCommand}
              onDraftChange={setNewCommand}
              placeholder="apt-get install -y mycli"
              emptyHint="No overrides yet. Add a command below to customize the base image for your organization."
            />
          </CanGuard>
          <DirtyActionBar
            dirty={dirty}
            saving={update.isPending}
            onDiscard={handleDiscard}
            onSave={handleSave}
            saveLabel="Save overrides"
            permission="blueprints:write"
          />
        </LayerCard>

        {/* Architecture — its own grouping alongside the three command
            layers: it changes what the whole image is built FOR rather than
            what runs inside it. Save-gated independently of the command
            editor so flipping it is always a deliberate, single-purpose act. */}
        <LayerCard
          icon={<Cpu />}
          name="Architecture"
          description="CPU architecture the base image is built for. Every blueprint and devbox in the organization inherits it."
        >
          <CanGuard
            permission="blueprints:write"
            fallback={
              <p className="text-sm text-muted-foreground">
                Base image architecture:{' '}
                <span className="font-mono text-foreground/90">{data.architecture}</span>. Requires
                blueprint access to change.
              </p>
            }
          >
            <div className="space-y-3">
              <Select
                value={architecture}
                onValueChange={(value) => setArchitecture(value as BlueprintArchitecture)}
              >
                <SelectTrigger
                  size="sm"
                  className="h-7 w-32 text-xs"
                  aria-label="Base image architecture"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="x86_64">x86_64</SelectItem>
                  <SelectItem value="arm64">arm64</SelectItem>
                </SelectContent>
              </Select>

              {architectureDirty && (
                <div className="flex items-start gap-2 rounded-md border border-status-warning/30 bg-status-warning/10 px-3 py-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-status-warning shrink-0 mt-0.5" />
                  <span>
                    Switching the architecture rebuilds base and cascades a rebuild of every
                    blueprint built on it. Devboxes launched from those blueprints will run{' '}
                    <span className="font-mono">{architecture}</span> after the rebuild completes.
                  </span>
                </div>
              )}
            </div>
          </CanGuard>
          <DirtyActionBar
            dirty={architectureDirty}
            saving={update.isPending}
            onDiscard={handleDiscardArchitecture}
            onSave={handleSaveArchitecture}
            saveLabel="Save architecture"
            permission="blueprints:write"
          />
        </LayerCard>

        {/* The composed result of all three layers, rendered as one
            standalone, buildable Dockerfile. */}
        {data.dockerfileExample && (
          <div className="flex justify-start pt-1">
            <BaseDockerfileDialog dockerfile={data.dockerfileExample} />
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

/**
 * Humanize a plugin package name for the summary list — strips the
 * `plugin-` prefix and title-cases the remaining words (e.g.
 * `plugin-tailscale` → `Tailscale`).
 */
function humanizePluginName(name: string): string {
  return (
    name
      .replace(/^plugin-/, '')
      .split(/[-_]/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ') || name
  );
}

/**
 * A single-line summary entry in a read-only layer. Shows only its short
 * label by default; clicking expands to reveal the full command (and an
 * optional description) so the layer reads as a scannable list until you
 * choose to dive in.
 */
function DisclosureRow({
  label,
  description,
  trailing,
  children,
}: {
  label: string;
  description?: string;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left outline-none hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <ChevronRight
            className={cn(
              'h-3 w-3 shrink-0 text-muted-foreground/60 transition-transform',
              open && 'rotate-90',
            )}
          />
          <span className="min-w-0 flex-1 truncate text-sm">
            <span className="text-foreground">{label}</span>
            {description && <span className="ml-1.5 text-muted-foreground/70">{description}</span>}
          </span>
          {trailing}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-1.5 pt-1 pr-1 pb-1.5 pl-5">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * A base layer rendered as a compact card: an icon, the layer name, a
 * command-count badge, and a one-line description.
 *
 * Two modes:
 *   - Collapsible (default) — header is a toggle and the command list
 *     reveals on expand. Used for the read-only core/plugin layers, which
 *     start collapsed, and the editable Organization overrides layer,
 *     which starts expanded via `defaultOpen` since it is the only
 *     customizable layer (and is `accent`ed for prominence).
 *   - Static (`collapsible={false}`) — header has no toggle and the body
 *     is always visible.
 */
function LayerCard({
  icon,
  name,
  description,
  docsSlug,
  count,
  collapsible = true,
  defaultOpen = false,
  accent = false,
  children,
}: {
  icon: ReactNode;
  name: string;
  description: string;
  docsSlug?: string;
  count?: number;
  collapsible?: boolean;
  defaultOpen?: boolean;
  accent?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  const containerClass = cn(
    'overflow-hidden rounded-lg border',
    accent ? 'border-border bg-card/40' : 'border-border/60 bg-muted/20',
  );

  const header = (
    <>
      <span className="shrink-0 text-muted-foreground [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
      <span className="text-sm font-medium text-foreground">{name}</span>
      {docsSlug && (
        <span onClick={(e) => e.stopPropagation()}>
          <InlineDocsHint slug={docsSlug} />
        </span>
      )}
      {count !== undefined && (
        <span className="rounded-full bg-muted px-1.5 py-px text-xs font-medium tabular-nums text-muted-foreground">
          {count}
        </span>
      )}
      {collapsible && (
        <ChevronDown
          className={cn(
            'ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground/60 transition-transform',
            !open && '-rotate-90',
          )}
        />
      )}
    </>
  );

  const body = (
    <div className="border-t border-border/40 px-3 py-2.5 space-y-2">
      <p className="text-sm text-muted-foreground">{description}</p>
      {children}
    </div>
  );

  if (!collapsible) {
    return (
      <div className={containerClass}>
        <div className="flex w-full items-center gap-2.5 px-3 py-2.5">{header}</div>
        {body}
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={containerClass}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="group flex w-full items-center gap-2.5 px-3 py-2.5 text-left outline-none hover:bg-muted/30 focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          {header}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>{body}</CollapsibleContent>
    </Collapsible>
  );
}
